"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import NativeLocation, {
  type TrackingState,
} from "@/plugins/native-location";

interface UseNativeLocationReturn {
  // State
  isReady: boolean;
  isTracking: boolean;
  totalMiles: number;
  lastPosition: { lat: number; lng: number } | null;
  lastUpdate: number | null;
  isStationary: boolean;
  isLowPowerMode: boolean;
  error: string | null;
  isNative: boolean;
  pointCount: number;

  // Actions
  startTracking: (options: {
    shiftId: string;
    initialLat?: number;
    initialLng?: number;
  }) => Promise<boolean>;
  stopTracking: () => Promise<{
    totalMiles: number;
    pointCount: number;
    duration: number;
  } | null>;
  resumeTracking: (shiftId: string) => Promise<{
    resumed: boolean;
    restoredMiles: number;
  }>;
  getCurrentPosition: () => Promise<{ lat: number; lng: number } | null>;
  getLocationPoints: (
    sessionId: string
  ) => Promise<Array<{ lat: number; lng: number; timestamp: number }>>;
  clearSession: (sessionId: string) => Promise<void>;
}

const initialTrackingState: TrackingState = {
  isTracking: false,
  sessionId: null,
  totalMiles: 0,
  lastLat: null,
  lastLng: null,
  lastUpdate: null,
  isStationary: false,
  isLowPowerMode: false,
};

export function useNativeLocation(): UseNativeLocationReturn {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackingState, setTrackingState] =
    useState<TrackingState>(initialTrackingState);
  const [pointCount, setPointCount] = useState(0);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isNative = Capacitor.isNativePlatform();

  // Initialize and check platform
  useEffect(() => {
    const init = async () => {
      try {
        // Get initial state
        const state = await NativeLocation.getTrackingState();
        setTrackingState(state);

        // If tracking, get point count
        if (state.isTracking && state.sessionId) {
          const result = await NativeLocation.getLocationPoints({
            sessionId: state.sessionId,
          });
          setPointCount(result.points.length);
        }

        setIsReady(true);
      } catch (err) {
        console.error("Failed to initialize NativeLocation:", err);
        setError(
          err instanceof Error ? err.message : "Failed to initialize location"
        );
        setIsReady(true); // Still mark ready, web fallback will work
      }
    };

    init();
  }, []);

  // Poll for state updates when tracking
  useEffect(() => {
    if (!trackingState.isTracking) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const state = await NativeLocation.getTrackingState();
        console.log("[LocationPoll] State:", JSON.stringify({
          isTracking: state.isTracking,
          sessionId: state.sessionId,
          totalMiles: state.totalMiles,
          lastLat: state.lastLat,
          lastLng: state.lastLng,
          lastUpdate: state.lastUpdate,
        }));
        setTrackingState(state);

        // Update point count
        if (state.sessionId) {
          const result = await NativeLocation.getLocationPoints({
            sessionId: state.sessionId,
          });
          console.log("[LocationPoll] Points count:", result.points.length);
          setPointCount(result.points.length);
        }
      } catch (err) {
        console.error("Error polling tracking state:", err);
      }
    };

    // Poll immediately on start, then every 5 seconds
    poll();
    pollIntervalRef.current = setInterval(poll, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [trackingState.isTracking]);

  const startTracking = useCallback(
    async (options: {
      shiftId: string;
      initialLat?: number;
      initialLng?: number;
    }): Promise<boolean> => {
      try {
        setError(null);
        console.log("[LocationTracker] Starting tracking:", options.shiftId);
        const result = await NativeLocation.startTracking({
          sessionId: options.shiftId,
          initialLat: options.initialLat,
          initialLng: options.initialLng,
        });
        console.log("[LocationTracker] Start result:", result.success);

        if (result.success) {
          const state = await NativeLocation.getTrackingState();
          console.log("[LocationTracker] Initial state after start:", JSON.stringify(state));
          setTrackingState(state);
          setPointCount(1); // Initial point
        } else {
          console.warn("[LocationTracker] startTracking returned false - check native logs for authorization issues");
        }

        return result.success;
      } catch (err) {
        console.error("Failed to start tracking:", err);
        setError(
          err instanceof Error ? err.message : "Failed to start tracking"
        );
        return false;
      }
    },
    []
  );

  const stopTracking = useCallback(async () => {
    try {
      const result = await NativeLocation.stopTracking();

      setTrackingState(initialTrackingState);
      setPointCount(0);

      return {
        totalMiles: result.totalMiles,
        pointCount: result.pointCount,
        duration: result.duration,
      };
    } catch (err) {
      console.error("Failed to stop tracking:", err);
      setError(err instanceof Error ? err.message : "Failed to stop tracking");
      return null;
    }
  }, []);

  const getCurrentPosition = useCallback(async (): Promise<{
    lat: number;
    lng: number;
  } | null> => {
    // For native, try to use last known position first
    if (trackingState.lastLat !== null && trackingState.lastLng !== null) {
      return { lat: trackingState.lastLat, lng: trackingState.lastLng };
    }

    // Fall back to browser geolocation
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    });
  }, [trackingState.lastLat, trackingState.lastLng]);

  const resumeTracking = useCallback(
    async (
      shiftId: string
    ): Promise<{ resumed: boolean; restoredMiles: number }> => {
      try {
        // Get current state first
        const currentState = await NativeLocation.getTrackingState();

        // If already tracking this session, just return the current miles
        if (currentState.isTracking && currentState.sessionId === shiftId) {
          setTrackingState(currentState);
          return { resumed: true, restoredMiles: currentState.totalMiles };
        }

        // Try to resume existing session from native database
        // This handles the case where iOS terminated the app but the session data persists
        const resumeResult = await NativeLocation.resumeSession({ sessionId: shiftId });
        if (resumeResult.success) {
          // Session was restored from database with accumulated miles
          const state = await NativeLocation.getTrackingState();
          setTrackingState(state);
          console.log("Restored session from database with", resumeResult.restoredMiles, "miles");
          return { resumed: true, restoredMiles: resumeResult.restoredMiles };
        }

        // Session doesn't exist in database - start fresh tracking
        // This happens if the session was never started or was already cleared
        const position = await getCurrentPosition();
        if (!position) {
          return { resumed: false, restoredMiles: 0 };
        }

        const success = await startTracking({
          shiftId,
          initialLat: position.lat,
          initialLng: position.lng,
        });

        return { resumed: success, restoredMiles: 0 };
      } catch (err) {
        console.error("Failed to resume tracking:", err);
        return { resumed: false, restoredMiles: 0 };
      }
    },
    [getCurrentPosition, startTracking]
  );

  const getLocationPoints = useCallback(
    async (
      sessionId: string
    ): Promise<Array<{ lat: number; lng: number; timestamp: number }>> => {
      try {
        const result = await NativeLocation.getLocationPoints({ sessionId });
        return result.points.map((p) => ({
          lat: p.lat,
          lng: p.lng,
          timestamp: p.timestamp,
        }));
      } catch (err) {
        console.error("Failed to get location points:", err);
        return [];
      }
    },
    []
  );

  const clearSession = useCallback(async (sessionId: string): Promise<void> => {
    try {
      await NativeLocation.clearSession({ sessionId });
    } catch (err) {
      console.error("Failed to clear session:", err);
    }
  }, []);

  return {
    // State
    isReady,
    isTracking: trackingState.isTracking,
    totalMiles: trackingState.totalMiles,
    lastPosition:
      trackingState.lastLat !== null && trackingState.lastLng !== null
        ? { lat: trackingState.lastLat, lng: trackingState.lastLng }
        : null,
    lastUpdate: trackingState.lastUpdate,
    isStationary: trackingState.isStationary,
    isLowPowerMode: trackingState.isLowPowerMode,
    error,
    isNative,
    pointCount,

    // Actions
    startTracking,
    stopTracking,
    resumeTracking,
    getCurrentPosition,
    getLocationPoints,
    clearSession,
  };
}
